import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { ScrollView, SxProp, View } from 'dripsy';
import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInput, UniInputAmount } from '@/design-system/components/inputs';
import { LayoutContext } from 'contexts/LayoutContext';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { TopUpStackParamList, TopUpStackRoutes } from './type';
import { AccountSelector } from '@/design-system/components/accountSelector/AccountSelector';
import { Card, Xmetadata } from '@utx/utx-access-client';
import { keyGenerator } from '@/utils/keyGenerator';
import { WalletData } from '@/design-system/components/accountSelector/useAccountSelector';

export interface TopUpProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  onData: (data: any) => void;
  func?: (
    accountId: string,
    amount: number,
    currency: string,
    xmetadata: Xmetadata,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  record?: any;
}

export const TopUpWebZeroScreen: React.FC<TopUpProps> = (props) => {
  const { onClose, onLayout, onData, func, onReturnData, refresh, record } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const { body, btnView, textMb, btn } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<TopUpStackParamList>>();

  const [xmetadata, setXmetadata] = useState<Array<{ key: string; value: string }>>([]);
  const [account, setAccount] = useState<any>({
    accountId: undefined,
    amount: undefined,
    currency: record?.currency,
    xmetadata: {},
    wallet: undefined,
    name: record?.name
  });
  const [errorMsg, setErrorMsg] = useState<any>({ amount: undefined, accountId: undefined, xmetadata: undefined });

  const [key, setKey] = useState('');
  useFocusEffect(
    React.useCallback(() => {
      setKey(keyGenerator('withdraw'));
    }, [])
  );

  const validateForm = async (data?: { [key: string]: string | undefined }, wallet?: WalletData | undefined) => {
    const keys = data ? Object.keys(data) : [];
    const keysLength = keys.length;
    let validateFlag = true;
    let errorMsgFlag = { ...errorMsg };

    if ((!keysLength && !account.accountId) || (keys.includes('accountId') && !data?.accountId)) {
      errorMsgFlag.accountId = t('card-account-management::glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && account.accountId) || (keys.includes('accountId') && data?.accountId)) {
      errorMsgFlag.accountId = undefined;
    }

    if ((!keysLength && !account.amount) || (keys.includes('amount') && !data?.amount)) {
      errorMsgFlag.amount = t('card-account-management::glossary.field-required');
      validateFlag = false;
    } else if ((!keysLength && account.amount) || (keys.includes('amount') && data?.amount)) {
      if (
        (!wallet && !keysLength && account.amount && account.wallet && Number(account.wallet.balance) < Number(account.amount)) ||
        (!wallet && keys.includes('amount') && data?.amount && account.wallet && Number(account.wallet.balance) < Number(data?.amount)) ||
        (keys.includes('amount') && data?.amount && wallet && wallet?.balance && Number(wallet?.balance) < Number(data?.amount))
      ) {
        errorMsgFlag.amount = t('card-account-management::glossary.insufficient-balance');
        validateFlag = false;
      } else errorMsgFlag.amount = undefined;
    }

    if ((!keysLength && xmetadata.length) || (keys.includes('xmetadata') && xmetadata.length)) {
      let xmetadataErrorMsg: Array<{ key: string; value: string }> = [];
      xmetadata.forEach((x, i) => {
        const xd = xmetadata.find((xm, xmi) => xm.key == x.key && i != xmi);
        if (!x.key) {
          xmetadataErrorMsg[i] = { ...xmetadataErrorMsg[i], key: t('card-account-management::glossary.field-required') };
          validateFlag = false;
        } else if (xd) {
          xmetadataErrorMsg[i] = { ...xmetadataErrorMsg[i], key: t('card-account-management::glossary.key-already-exists') };
          validateFlag = false;
        }
      });

      errorMsgFlag.xmetadata = xmetadataErrorMsg;
    }

    setErrorMsg(errorMsgFlag);
    return validateFlag;
  };

  const changeTopUp = async () => {
    const validate = await validateForm();
    if (validate) {
      xmetadata.forEach((x) => (account.xmetadata[x.key] = x.value));
      onData({ data: account });
      navigation.navigate(TopUpStackRoutes.Confirm, {
        onLayout,
        data: account,
        func,
        onReturnData,
        refresh
      });
    }
  };

  const onChange = (value: string) => {
    setAccount({ ...account, accountId: value });
  };

  const walletChange = (value: any) => {
    setAccount({ ...account, wallet: value });
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}
      key={key}>
      <ModalHeader title={t('card-account-management::top-up-form.title--top-up')} closeModal={onClose} />
      <ScrollView sx={{ flex: 1, backgroundColor: 'transparent' }} nestedScrollEnabled>
        <View sx={body}>
          <TextTitle sx={textMb} semiBold>
            {t('card-account-management::top-up-form.text--fill-out-form-to-top-up-account')}
          </TextTitle>
          <View sx={{ gap: '$5' }}>
            <View sx={{ gap: '$1', zIndex: 99 }}>
              <TextCaption semiBold sx={{ mb: '$1' }}>
                {t('card-account-management::glossary.select-account')}
              </TextCaption>
              <AccountSelector
                asset={record?.currency}
                onValueChange={(value) => {
                  onChange(value);
                }}
                onWalletChange={(value) => {
                  walletChange(value);
                  if (account?.amount) validateForm({ accountId: value?.value, amount: account?.amount }, value);
                  else validateForm({ accountId: value?.value });
                }}
                value={account?.accountId}
                showBalance={true}
                sx={{ mb: 0 }}
                dropdownSx={{ maxHeight: 185 }}
              />
              {errorMsg?.accountId && (
                <TextCaption danger sx={{ mt: '$1' }}>
                  {errorMsg?.accountId}
                </TextCaption>
              )}
            </View>
            <UniInputAmount
              labelText={t('card-account-management::glossary.amount')}
              assetPrefix={false}
              maxFlag={false}
              sxWrap={{ alignItems: 'center', flexDirection: 'row-reverse' }}
              asset={account.currency}
              errorText={errorMsg.amount}
              value={account.amount}
              decimals={2}
              onValueChange={(value) => {
                setAccount({
                  ...account,
                  amount: value
                });
                validateForm({ amount: value?.toString() });
              }}
            />
            <View sx={{ gap: '$1' }}>
              <View sx={{ gap: 10, flexDirection: 'row' }}>
                <TextCaption semiBold sx={{ mb: '$1' }}>
                  {t('card-account-management::glossary.xmetadata')}
                </TextCaption>
                <UniIconButton
                  icon="add"
                  onPress={() => {
                    setXmetadata([...xmetadata, { key: '', value: '' }]);
                  }}
                />
              </View>
              {xmetadata.map((x, i) => (
                <View sx={{ gap: '$3', my: '$1', flexDirection: 'row', alignItems: 'center' }}>
                  <UniInput
                    placeholder={t('card-account-management::glossary.please-input-key')}
                    errorText={errorMsg.xmetadata && errorMsg.xmetadata[i] ? errorMsg.xmetadata[i].key : ''}
                    onValueChange={(value) => {
                      let xmetadataFlag = xmetadata;
                      xmetadataFlag[i].key = value || '';
                      setXmetadata(xmetadataFlag);
                      validateForm({ xmetadata: value });
                    }}
                  />
                  <UniInput
                    placeholder={t('card-account-management::glossary.please-input-value')}
                    onValueChange={(value) => {
                      let xmetadataFlag = xmetadata;
                      xmetadataFlag[i].value = value || '';
                      setXmetadata(xmetadataFlag);
                    }}
                    wrapperSx={{ mb: errorMsg.xmetadata && errorMsg.xmetadata[i] && errorMsg.xmetadata[i].key ? '$5' : undefined }}
                  />
                  <UniIconButton
                    icon="minus"
                    onPress={() => {
                      let xmetadataFlag: Array<{ key: string; value: string }> = [];
                      let xmetadataErrorMsg: Array<{ key: string; value: string }> = errorMsg.xmetadata;
                      xmetadata.forEach((xd, xdi) => {
                        if (xdi != i) xmetadataFlag.push(xd);
                        if (xmetadataErrorMsg && xmetadataErrorMsg[i] && xmetadataErrorMsg[i].key) xmetadataErrorMsg[i].key = '';
                      });
                      setXmetadata(xmetadataFlag);
                      setErrorMsg({ ...errorMsg, xmetadata: xmetadataErrorMsg });
                    }}
                    sx={{ mb: errorMsg.xmetadata && errorMsg.xmetadata[i] && errorMsg.xmetadata[i].key ? '$5' : undefined }}
                  />
                </View>
              ))}
            </View>
          </View>
          <View sx={btnView}>
            <UniButton
              sx={btn}
              variant={ButtonVariant.outline1}
              label={t('card-account-management::general.button--cancel')}
              onPress={onClose}
            />
            <UniButton
              sx={btn}
              labelSx={{ color: '$balck' }}
              label={t('card-account-management::change-atm-pin.button--update')}
              onPress={changeTopUp}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    marginTop: 40,
    zIndex: -1
  },
  btn: {
    width: 216,
    height: 44
  }
};
